"use client";

import { Locale } from "@/types/locale";
import React, { useCallback, useMemo, useState } from "react";
import { useAnyTranslations } from "@lib/i18n/utils";
import { cn } from "@lib/utils";
import { ProgramProposalEntity } from "@features/events-program/types/program-proposal";
import { AssignmentMemberDisplayLookup } from "@features/events-program/ui/assigments/assignment-member-display";
import {
  buildProposalVoteAverageRatingText,
  buildProposalVoteDisplays,
  buildProposalVoteResolutionSummary,
  ProposalVoteDisplay,
} from "./proposal-vote-display";
import {
  ProposalVotesHiddenContent,
  ProposalVotesHiddenContentLabels,
} from "./proposal-votes-hidden-content";
import { ProposalVoteDetailsVisibility } from "./proposal-votes-hover-card";
import {
  ProposalVotesSummaryContent,
  ProposalVotesSummaryLabels,
} from "./proposal-votes-summary-content";

type ProposalVotesDetailsBlockProps = {
  proposal: ProgramProposalEntity;
  locale: Locale;
  voteDisplayLookup?: AssignmentMemberDisplayLookup;
  currentTeamMemberIds?: readonly number[];
  detailsVisibility?: ProposalVoteDetailsVisibility;
  hiddenLabels: ProposalVotesHiddenContentLabels;
  onEditVote?: (voterId: number) => void;
  canEditVote?: (vote: ProposalVoteDisplay) => boolean;
  editingVoteId?: number;
  className?: string;
};

const buildSummaryLabels = (
  tVoteSummary: ReturnType<typeof useAnyTranslations>,
): ProposalVotesSummaryLabels => ({
  title: tVoteSummary("title"),
  votersCount: (count) => tVoteSummary("votersCount", { count }),
  detailsTitle: tVoteSummary("detailsTitle"),
  emptySummary: tVoteSummary("emptySummary"),
  emptyComment: tVoteSummary("emptyComment"),
  scoreLabel: tVoteSummary("scoreLabel"),
  openProposal: tVoteSummary("openProposal"),
  proxyVoteBadge: tVoteSummary("proxyVoteBadge"),
  myVoteBadge: tVoteSummary("myVoteBadge"),
  averageRatingLabel: tVoteSummary("averageRatingLabel"),
  editVoteAction: tVoteSummary("editVoteAction"),
  needsReview: tVoteSummary("needsReview"),
});

export const ProposalVotesDetailsBlock = React.memo<ProposalVotesDetailsBlockProps>(
  ({
    proposal,
    locale,
    voteDisplayLookup,
    currentTeamMemberIds,
    detailsVisibility,
    hiddenLabels,
    onEditVote,
    canEditVote,
    editingVoteId,
    className,
  }) => {
    const tVoteSummary = useAnyTranslations(
      "features.events-program.ui.tables.eventVotingPanelTable.voteSummary",
    );
    const [locallyRevealed, setLocallyRevealed] = useState(false);
    const detailsVisible = locallyRevealed || detailsVisibility?.detailsVisible !== false;
    const handleRevealSingleProposal = useCallback(() => {
      setLocallyRevealed(true);
      detailsVisibility?.onRevealSingleProposal?.();
    }, [detailsVisibility]);
    const labels = useMemo(() => buildSummaryLabels(tVoteSummary), [tVoteSummary]);
    const votes = useMemo(
      () =>
        !detailsVisible
          ? []
          : buildProposalVoteDisplays(proposal, {
              locale,
              tVoteSummary,
              voteDisplayLookup,
              currentTeamMemberIds,
            }),
      [currentTeamMemberIds, detailsVisible, locale, proposal, tVoteSummary, voteDisplayLookup],
    );
    const summary = useMemo(() => buildProposalVoteResolutionSummary(votes), [votes]);
    const averageRatingText = useMemo(
      () => buildProposalVoteAverageRatingText(proposal.votes, locale),
      [locale, proposal.votes],
    );

    if (!detailsVisible) {
      return (
        <div className={cn("mb-4 rounded-md border bg-background", className)}>
          <ProposalVotesHiddenContent
            labels={hiddenLabels}
            canRevealSingleProposal={detailsVisibility?.canRevealSingleProposal}
            onRevealSingleProposal={handleRevealSingleProposal}
            className="w-full shadow-xs"
          />
        </div>
      );
    }

    return (
      <div className={cn("mb-4 rounded-md border bg-background", className)}>
        <ProposalVotesSummaryContent
          votes={votes}
          summary={summary}
          labels={labels}
          className="w-full shadow-xs"
          previewComments={false}
          averageRatingText={averageRatingText}
          onEditVote={onEditVote}
          canEditVote={canEditVote}
          editingVoteId={editingVoteId}
        />
      </div>
    );
  },
);

ProposalVotesDetailsBlock.displayName = "ProposalVotesDetailsBlock";
